import {
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    StyleSheet,
    Image,
    View
} from "react-native";
import {
    SAMPLE_PHOTO_SOURCE,
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    HighlightButton,
    NCoreUIKitTheme,
    AspectRatio
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    ChevronRightIcon,
    HomeIcon,
    StarIcon,
    PlusIcon
} from "../../anatomy/icons";

const NAME = "HighlightButton";

const USAGE_CODE = `const [isAdding, setIsAdding] = useState(false);

<HighlightButton
    onPress={() => setIsAdding(true)}
    isLoading={isAdding}
    icon={PlusIcon}
    title="Add"
/>`;

const TYPES = [
    "neutral",
    "primary",
    "success",
    "warning",
    "danger",
    "info",
    "glass"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const LOADING_DELAY = 1200;

const stylesheet = StyleSheet.create({
    overlay: {
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap",
        flex: 1
    },
    photo: {
        position: "absolute",
        height: "100%",
        width: "100%"
    },
    backdrop: {
        width: "100%"
    }
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("highlightButtonPage.typesTitle"),
        id: "types"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("highlightButtonPage.iconTitle"),
        id: "icon"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const useTimedLoading = () => {
    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    useEffect(() => {
        if(!isLoading) {
            return;
        }

        const timer = window.setTimeout(() => setIsLoading(false), LOADING_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoading
    ]);

    const handleStart = () => setIsLoading(true);

    return {
        handleStart,
        isLoading
    };
};

const HighlightButtonPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        handleStart: handleAdd,
        isLoading: isAdding
    } = useTimedLoading();

    const {
        handleStart: handleSend,
        isLoading: isSending
    } = useTimedLoading();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const renderBackdrop = (children: ReactNode) => <AspectRatio
        alt={t("highlightButtonPage.backdropAlt")}
        style={stylesheet.backdrop}
        ratio="21:9"
    >
        <Image
            source={SAMPLE_PHOTO_SOURCE}
            style={stylesheet.photo}
            aria-hidden={true}
        />
        <View
            style={[
                stylesheet.overlay,
                {
                    padding: spaces.spacingLg,
                    gap: spaces.spacingSm
                }
            ]}
        >
            {children}
        </View>
    </AspectRatio>;

    return <Article>
        <ComponentHeader
            lead={t("highlightButtonPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("highlightButtonPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("highlightButtonPage.overviewCaption")}
            >
                {
                    renderBackdrop(<>
                        <HighlightButton
                            title={t("highlightButtonPage.add")}
                            isLoading={isAdding}
                            onPress={handleAdd}
                            icon={PlusIcon}
                        />
                        <HighlightButton
                            title={t("highlightButtonPage.favorite")}
                            onPress={() => undefined}
                            icon={StarIcon}
                            type="glass"
                        />
                    </>)
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("highlightButtonPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("highlightButtonPage.typesText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <HighlightButton
                        isPlainBackground={type === "glass"}
                        onPress={() => undefined}
                        title={type}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("highlightButtonPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <HighlightButton
                        onPress={() => undefined}
                        icon={PlusIcon}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("highlightButtonPage.iconTitle")}
            id="icon"
        >
            <Paragraph>
                {t("highlightButtonPage.iconText")}
            </Paragraph>
            <Preview>
                <HighlightButton
                    title={t("highlightButtonPage.continue")}
                    onPress={() => undefined}
                    icon={ChevronRightIcon}
                    iconDirection="end"
                />
                <HighlightButton
                    accessibilityLabel={t("sample.home")}
                    onPress={() => undefined}
                    icon={HomeIcon}
                    type="primary"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("highlightButtonPage.statesText")}
            </Paragraph>
            <Preview>
                <HighlightButton
                    title={t("highlightButtonPage.disabled")}
                    onPress={() => undefined}
                    isDisabled={true}
                />
                <HighlightButton
                    title={t("highlightButtonPage.send")}
                    isLoading={isSending}
                    onPress={handleSend}
                />
                {
                    renderBackdrop(<HighlightButton
                        title={t("highlightButtonPage.favorite")}
                        onPress={() => undefined}
                        isDisabled={true}
                        icon={StarIcon}
                        type="glass"
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("highlightButtonPage.a11yRole"),
                    t("highlightButtonPage.a11yKeyboard"),
                    t("highlightButtonPage.a11yDisabled"),
                    t("highlightButtonPage.a11yLoading"),
                    t("highlightButtonPage.a11yIconOnly")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("highlightButtonPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("highlightButtonPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "\"neutral\" | \"primary\" | \"success\" | \"warning\" | \"danger\" | \"info\" | \"glass\"",
                        description: t("highlightButtonPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"xSmall\" | \"small\" | \"medium\" | \"large\"",
                        description: t("highlightButtonPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("highlightButtonPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("highlightButtonPage.propIconDirection"),
                        type: "\"start\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "iconDirection"
                    },
                    {
                        description: t("highlightButtonPage.propIconSize"),
                        name: "iconSize",
                        type: "number"
                    },
                    {
                        description: t("highlightButtonPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("highlightButtonPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("highlightButtonPage.propIsPressScaleEnabled"),
                        name: "isPressScaleEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("highlightButtonPage.propRenderLoading"),
                        type: "() => ReactNode",
                        name: "renderLoading"
                    },
                    {
                        description: t("highlightButtonPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("highlightButtonPage.propBorderRadius"),
                        name: "borderRadius",
                        type: "number"
                    },
                    {
                        description: t("highlightButtonPage.propIsCustomPadding"),
                        name: "isCustomPadding",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("highlightButtonPage.propCustomColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "customColor"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("highlightButtonPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("highlightButtonPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("highlightButtonPage.propAndroidDisableSound"),
                        name: "android_disableSound",
                        type: "boolean"
                    },
                    {
                        description: t("highlightButtonPage.propGlassEffect"),
                        name: "glassEffect",
                        defaultValue: "12",
                        type: "number"
                    },
                    {
                        description: t("highlightButtonPage.propIsPlainBackground"),
                        name: "isPlainBackground",
                        defaultValue: "false",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("highlightButtonPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default HighlightButtonPage;
